import { useEffect, useLayoutEffect } from 'react';

import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import useSheetsStore from 'storages/sheets/sheets';
import useSheetsTimerStore from 'storages/sheetsTimer/sheetsTimer';
import useSubjectStore from 'storages/subject';

const normalizeCurriculumKey = (curriculum: string | string[] | null): string | null => {
  if (Array.isArray(curriculum)) {
    return curriculum.length ? curriculum.join(',') : null;
  }

  return curriculum || null;
};

const buildSheetKey = (curriculum: string | string[] | null, year: string | null, month: number | null): string | null => {
  const curriculumKey = normalizeCurriculumKey(curriculum);

  if (!curriculumKey || !year || month == null) {
    return null;
  }

  return `${curriculumKey}|${year}|${month}`;
};

const useSheetsTimerSync = (): void => {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.SHEETS_TIMER);
  const { subject } = useSubjectStore();
  const { curriculum, year, month, fetched } = useSheetsStore();
  const { durationMinutes, initForSubject, startForSheet, stop, tick } = useSheetsTimerStore();

  useLayoutEffect(() => {
    if (!isEnabled || !subject?.subjectCode) {
      return;
    }

    initForSubject(subject.subjectCode);
  }, [initForSubject, isEnabled, subject?.subjectCode]);

  useEffect(() => {
    if (!isEnabled) {
      stop();
      return;
    }

    const sheetKey = buildSheetKey(curriculum, year, month);
    if (!fetched || !sheetKey || durationMinutes == null) {
      stop();
      return;
    }

    startForSheet(sheetKey);
  }, [curriculum, durationMinutes, fetched, isEnabled, month, startForSheet, stop, year]);

  useEffect(() => {
    if (!isEnabled || durationMinutes == null) {
      return undefined;
    }

    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [durationMinutes, isEnabled, tick]);
};

export default useSheetsTimerSync;
